import type { ProductColor, SiteRating } from '@aloft/toolkit-shared';
import { isUndefined } from '@aloft/toolkit-shared';
import { colors } from '@aloft/toolkit-sui';

import type { LabelProps } from 'recharts';
import { Bar, CartesianGrid, ComposedChart, Customized, LabelList, Line } from 'recharts';

import { GraphTitle } from '../components/GraphTitle.tsx';
import { GraphXAxis } from '../components/GraphXAxis.tsx';
import { GraphYAxis } from '../components/GraphYAxis.tsx';
import { ResponsiveGraphWrapper } from '../components/ResponsiveGraphWrapper.tsx';
import { SimpleGraphTooltip } from '../components/SimpleGraphTooltip.tsx';
import { SiteValueRechartsSvgLegend } from '../components/SiteValueRechartsSvgLegend.tsx';
import type { GraphProps } from '../types.ts';

export type SiteSizeVsSiteValueGraphChartData = {
  propertyName: string;
  label: string;
  influenceRating?: SiteRating;
  viewRating?: SiteRating;
  siteSize?: number;
  siteValue?: number;
  compNumber?: number;
};

const getRatingColor = (rating?: SiteRating): ProductColor => {
  switch (rating) {
    case 'Adverse':
      return 'graphAdverse';
    case 'Neutral':
      return 'graphNeutral';
    case 'Beneficial':
    default:
      return 'graphBeneficial';
  }
};

const CustomLabelList = ({ x, y, width, value }: LabelProps) => {
  if (!value || isUndefined(x) || isUndefined(y) || isUndefined(width)) return null;

  const boxWidth = 28;
  const boxHeight = 18;
  const cx = Number(x) + Number(width) / 2 - boxWidth / 2;
  const cy = Number(y) - boxHeight / 2;

  let color = colors.graphBeneficial;
  if (String(value).includes('A')) color = colors.graphAdverse;
  else if (String(value).includes('N')) color = colors.graphNeutral;

  return (
    <g>
      <rect
        x={cx}
        y={cy}
        rx={3}
        ry={3}
        width={boxWidth}
        height={boxHeight}
        fill={colors.graphBackground}
        stroke={color}
      />
      <text
        x={cx + boxWidth / 2}
        y={cy + boxHeight / 2 + 4}
        textAnchor="middle"
        fontSize="12"
        fill={color}
      >
        {value}
      </text>
    </g>
  );
};

export type SiteSizeVsSiteValueGraphChartProps = GraphProps & {
  data: SiteSizeVsSiteValueGraphChartData[];
  showTitle?: boolean;
};

export const SiteSizeVsSiteValueGraphChart = ({
  data,
  disableAnimations,
  width,
  height,
  showTitle = true,
}: SiteSizeVsSiteValueGraphChartProps) => (
  <ResponsiveGraphWrapper disableAnimations={disableAnimations} width={width} height={height}>
    <ComposedChart
      data={data}
      width={width}
      height={height}
      margin={{ top: showTitle ? 48 : 12, left: 8, bottom: 56, right: 8 }}
      style={{ background: colors.graphBackground, borderRadius: 8 }}
    >
      {showTitle && <GraphTitle>Site Size vs Site Value</GraphTitle>}
      <CartesianGrid vertical={false} yAxisId="siteSize" />
      <Customized component={SiteValueRechartsSvgLegend} />

      <SimpleGraphTooltip<SiteSizeVsSiteValueGraphChartData>
        disableAnimations={disableAnimations}
        titleKey="propertyName"
        rows={[
          {
            label: 'Site Size',
            key: 'siteSize',
            color: 'graphChart3',
            valueFormatter: (v) => (v ? `${v.toLocaleString()} SF` : '-'),
          },
          {
            label: 'Site Value',
            key: 'siteValue',
            color: 'graphChart',
            valueFormatter: (v) => (v ? `$${v.toLocaleString()}` : '-'),
          },
          {
            label: 'Influence Rating',
            key: 'influenceRating',
            color: ({ influenceRating }) => getRatingColor(influenceRating),
          },
          {
            label: 'View Rating',
            key: 'viewRating',
            color: ({ viewRating }) => getRatingColor(viewRating),
          },
        ]}
      />

      <GraphXAxis dataKey="propertyName" />
      <GraphYAxis
        yAxisId="siteSize"
        label="Site Size (SF)"
        tickFormatter={(val: number) => val.toLocaleString()}
      />
      <GraphYAxis
        yAxisId="siteValue"
        position="right"
        label="Site Value"
        tickFormatter={(val: number) => `$${val.toLocaleString()}`}
      />

      <Bar
        name="Site Value"
        dataKey="siteValue"
        yAxisId="siteValue"
        fill={colors.graphChart}
        isAnimationActive={!disableAnimations}
      >
        <LabelList dataKey="label" content={CustomLabelList} />
      </Bar>
      <Line
        name="Site Size"
        dataKey="siteSize"
        yAxisId="siteSize"
        stroke={colors.graphChart3}
        isAnimationActive={!disableAnimations}
      />
    </ComposedChart>
  </ResponsiveGraphWrapper>
);
